import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { logout } from '@/lib/auth';
import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { LogOut, LayoutDashboard, Megaphone, Wand2, Phone, PhoneCall, BarChart3, Eye, X, User } from 'lucide-react';
import herculesLogo from '@/assets/hercules-logo.png';


interface Client {
  id: string;
  name: string;
}

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasCap, user: authUser } = useAuth();
  const [viewAsClientId, setViewAsClientId] = useState<string | null>(
    localStorage.getItem('viewAsClientId'),
  );
  const [viewAsClientName, setViewAsClientName] = useState<string>(
    localStorage.getItem('viewAsClientName') || '',
  );
  const [clients, setClients] = useState<Client[]>([]);

  // Derived from capabilities
  const viewAsActive = Boolean(viewAsClientId);
  const isRealAdmin = authUser?.user_type === 'system_admin';

  // Load clients list for admin dropdown
  useEffect(() => {
    if (!isRealAdmin) return;
    apiRequest<Client[]>('/auth/clients').then(setClients).catch(() => {});
  }, [isRealAdmin]);

  const startViewAs = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return;
    localStorage.setItem('viewAsClientId', clientId);
    localStorage.setItem('viewAsClientName', client.name);
    setViewAsClientId(clientId);
    setViewAsClientName(client.name);
    navigate('/');
    window.location.reload();
  };

  const stopViewAs = () => {
    localStorage.removeItem('viewAsClientId');
    localStorage.removeItem('viewAsClientName');
    setViewAsClientId(null);
    setViewAsClientName('');
    navigate('/');
    window.location.reload();
  };

  const handleLogout = async () => {
    localStorage.removeItem('viewAsClientId');
    localStorage.removeItem('viewAsClientName');
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, show: true },
    { path: '/calls', label: 'Calls', icon: Phone, show: hasCap('calls') },
    { path: '/insights', label: 'Insights', icon: BarChart3, show: hasCap('insights') },
    { path: '/my-agent', label: 'My Agent', icon: User, show: hasCap('my_agent') },
    { path: '/campaigns', label: 'Campaigns', icon: Megaphone, show: hasCap('campaigns') && !viewAsActive },
    { path: '/phone-numbers', label: 'Phone Numbers', icon: PhoneCall, show: hasCap('phone_numbers') && !viewAsActive },
    { path: '/studio', label: 'Studio', icon: Wand2, show: hasCap('studio') },
  ].filter((item) => item.show);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col border-r bg-card">
        {/* Logo */}
        <div className="border-b p-6">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={herculesLogo}
              alt="Hercules"
              className="teal-duotone h-10 w-auto"
            />
            <span className="text-xl font-bold text-foreground">Hercules</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 p-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Logout button at bottom */}
        <div className="border-t p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="w-full justify-start"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col">
        {/* Top bar */}
        <header className="border-b bg-card">
          <div className="flex items-center justify-between px-6 py-3">
            {/* View-as banner */}
            {viewAsClientId ? (
              <div className="flex items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-sm">
                <Eye className="h-4 w-4 text-amber-600" />
                <span className="text-amber-800 font-medium">
                  Viewing as: {viewAsClientName}
                </span>
                <button
                  onClick={stopViewAs}
                  className="ml-2 rounded p-1 hover:bg-amber-200 transition-colors"
                >
                  <X className="h-3.5 w-3.5 text-amber-600" />
                </button>
              </div>
            ) : (
              <div />
            )}

            {/* Admin: client selector */}
            {isRealAdmin && !viewAsClientId && clients.length > 0 && (
              <Select onValueChange={startViewAs}>
                <SelectTrigger className="w-56">
                  <Eye className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="View as client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
